import { useCallback, useRef, useState } from 'react'

const MIN_SCALE = 1.0
const MAX_SCALE = 3.0

export interface ZoomPanState {
  scale: number;
  offsetX: number;
  offsetY: number;
  /** CSS transform string to apply to the zoomable element. */
  transform: string;
  /** Pointer event handlers to spread onto the container element. */
  handlers: {
    onWheel: (e: React.WheelEvent<HTMLElement>) => void;
    onPointerDown: (e: React.PointerEvent<HTMLElement>) => void;
    onPointerMove: (e: React.PointerEvent<HTMLElement>) => void;
    onPointerUp: (e: React.PointerEvent<HTMLElement>) => void;
    onPointerCancel: (e: React.PointerEvent<HTMLElement>) => void;
  };
  /**
   * Convert a pointer position (relative to the container) into the
   * unscaled coordinate space of the content underneath.
   * Use this when mapping a click/tap to a zone.
   */
  toContentCoords: (x: number, y: number) => { x: number; y: number };
  reset: () => void;
}

interface ActivePointer {
  id: number;
  x: number;
  y: number;
}

function clampOffset (offsetX: number, offsetY: number, scale: number, containerW: number, containerH: number) {
  // When scale === 1, offset must be 0 (no panning needed).
  const maxX = (containerW * (scale - 1)) / 2
  const maxY = (containerH * (scale - 1)) / 2
  return {
    x: Math.max(-maxX, Math.min(maxX, offsetX)),
    y: Math.max(-maxY, Math.min(maxY, offsetY))
  }
}

/**
 * Zoom & pan hook (mouse wheel + two-finger pinch + drag to pan + double-click/tap to reset).
 * Scale is clamped to [1, 3]. Offset is clamped so content never scrolls outside its bounds.
 *
 * @param containerRef - Ref to the fixed-size container element.
 */
export function useZoomPan (containerRef: React.RefObject<HTMLElement>): ZoomPanState {
  const [scale, setScale] = useState(1)
  const [offsetX, setOffsetX] = useState(0)
  const [offsetY, setOffsetY] = useState(0)

  // Drag state
  const panStart = useRef<{ ox: number; oy: number; mx: number; my: number } | null>(null)
  const activePointers = useRef<ActivePointer[]>([])
  const lastPinchDist = useRef<number | null>(null)
  const lastTap = useRef<number>(0)
  const hasDragged = useRef(false)

  const reset = useCallback(() => {
    setScale(1)
    setOffsetX(0)
    setOffsetY(0)
  }, [])

  const toContentCoords = useCallback(
    (x: number, y: number) => ({
      x: (x - offsetX) / scale,
      y: (y - offsetY) / scale
    }),
    [scale, offsetX, offsetY]
  )

  // ── Wheel zoom ───────────────────────────────────────────────────────────────
  const onWheel = useCallback(
    (e: React.WheelEvent<HTMLElement>) => {
      e.preventDefault()
      const container = containerRef.current
      if (!container) return
      const rect = container.getBoundingClientRect()
      const factor = e.deltaY < 0 ? 1.12 : 1 / 1.12

      setScale((prev) => {
        const next = Math.max(MIN_SCALE, Math.min(MAX_SCALE, prev * factor))
        // Zoom toward cursor position
        const cx = e.clientX - rect.left - rect.width / 2
        const cy = e.clientY - rect.top - rect.height / 2
        const ratio = next / prev
        setOffsetX((ox) => {
          const nx = cx + (ox - cx) * ratio
          return clampOffset(nx, 0, next, rect.width, rect.height).x
        })
        setOffsetY((oy) => {
          const ny = cy + (oy - cy) * ratio
          return clampOffset(0, ny, next, rect.width, rect.height).y
        })
        return next
      })
    },
    [containerRef]
  )

  // ── Pointer down ─────────────────────────────────────────────────────────────
  const onPointerDown = useCallback(
    (e: React.PointerEvent<HTMLElement>) => {
      activePointers.current = [
        ...activePointers.current.filter((p) => p.id !== e.pointerId),
        { id: e.pointerId, x: e.clientX, y: e.clientY }
      ];
      (e.target as HTMLElement).setPointerCapture(e.pointerId)

      if (activePointers.current.length === 1) {
        // Single pointer — pan or double-tap
        const now = Date.now()
        if (now - lastTap.current < 300) {
          reset()
          lastTap.current = 0
        } else {
          lastTap.current = now
        }
        hasDragged.current = false
        setOffsetX((ox) => { panStart.current = { ox, oy: 0, mx: e.clientX, my: e.clientY }; return ox })
        setOffsetY((oy) => { if (panStart.current) panStart.current.oy = oy; return oy })
      } else {
        // Second finger — begin pinch
        panStart.current = null
        lastPinchDist.current = null
      }
    },
    [reset]
  )

  // ── Pointer move ─────────────────────────────────────────────────────────────
  const onPointerMove = useCallback(
    (e: React.PointerEvent<HTMLElement>) => {
      activePointers.current = activePointers.current.map((p) =>
        p.id === e.pointerId ? { ...p, x: e.clientX, y: e.clientY } : p
      )

      const container = containerRef.current
      if (!container) return
      const rect = container.getBoundingClientRect()

      if (activePointers.current.length >= 2) {
        // Pinch zoom
        const [a, b] = activePointers.current
        const dist = Math.hypot(a.x - b.x, a.y - b.y)
        if (lastPinchDist.current !== null) {
          const factor = dist / lastPinchDist.current
          setScale((prev) => {
            const next = Math.max(MIN_SCALE, Math.min(MAX_SCALE, prev * factor))
            const cx = (a.x + b.x) / 2 - rect.left - rect.width / 2
            const cy = (a.y + b.y) / 2 - rect.top - rect.height / 2
            const ratio = next / prev
            setOffsetX((ox) => clampOffset(cx + (ox - cx) * ratio, 0, next, rect.width, rect.height).x)
            setOffsetY((oy) => clampOffset(0, cy + (oy - cy) * ratio, next, rect.width, rect.height).y)
            return next
          })
        }
        lastPinchDist.current = dist
        return
      }

      // Pan (single pointer)
      if (!panStart.current) return
      const dx = e.clientX - panStart.current.mx
      const dy = e.clientY - panStart.current.my
      if (Math.abs(dx) > 3 || Math.abs(dy) > 3) hasDragged.current = true

      setScale((s) => {
        const clamped = clampOffset(panStart.current!.ox + dx, panStart.current!.oy + dy, s, rect.width, rect.height)
        setOffsetX(clamped.x)
        setOffsetY(clamped.y)
        return s
      })
    },
    [containerRef]
  )

  // ── Pointer up / cancel ───────────────────────────────────────────────────────
  const onPointerUp = useCallback((e: React.PointerEvent<HTMLElement>) => {
    activePointers.current = activePointers.current.filter((p) => p.id !== e.pointerId)
    if (activePointers.current.length < 2) {
      lastPinchDist.current = null
    }
    if (activePointers.current.length === 0) {
      panStart.current = null
    }
  }, [])

  const onPointerCancel = useCallback((e: React.PointerEvent<HTMLElement>) => {
    activePointers.current = activePointers.current.filter((p) => p.id !== e.pointerId)
    lastPinchDist.current = null
    panStart.current = null
  }, [])

  return {
    scale,
    offsetX,
    offsetY,
    transform: `translate(${offsetX}px, ${offsetY}px) scale(${scale})`,
    handlers: { onWheel, onPointerDown, onPointerMove, onPointerUp, onPointerCancel },
    toContentCoords,
    reset
  }
}
